import { z } from 'zod';

/**
 * Purchase Order — immutable commitment once issued. Items carry unit
 * price at the moment of issuance (we never recompute prices on read,
 * even if supplier raises rates, because that would change history).
 */
export const purchaseOrderStatusSchema = z.enum([
  'draft',
  'issued',
  'partially_received',
  'received',
  'cancelled',
]);
export type PurchaseOrderStatus = z.infer<typeof purchaseOrderStatusSchema>;

export const purchaseOrderItemSchema = z.object({
  // W147 · 2026-05-28 — `id` opcional por linha. Backend gera com newId()
  // ao persistir; create requests podem omitir. POs antigos (sem id) ainda
  // são aceitos no parse — frontend gera key sintética via description+index
  // como fallback (já em uso em ComprasPage W145). Quando todos os POs
  // legados forem re-emitidos, podemos tornar obrigatório.
  id: z.string().optional(),
  description: z.string().trim().min(1).max(200),
  quantity: z.number().min(0.001).max(999_999),
  unit: z.string().trim().min(1).max(20),
  unitPriceCents: z.number().int().min(0),
  // Wave 100 hardening — `subtotalCents` is now optional. The server
  // recomputes it as round(quantity * unitPriceCents) when omitted,
  // so a client posting only quantity+unitPrice gets accepted;
  // supplying it explicitly remains supported.
  subtotalCents: z.number().int().min(0).nullable().optional(),
  // Wave 11 added budget_item_id on purchase_order_lines. The Zod
  // schema was missing it, so Zod silently dropped the field — PMs
  // binding lines to budget rows from the UI saw the link disappear
  // after a round-trip.
  budgetItemId: z.string().nullable().optional(),
});
export type PurchaseOrderItem = z.infer<typeof purchaseOrderItemSchema>;

export const purchaseOrderSchema = z.object({
  id: z.string(),
  projectId: z.string(),
  purchaseRequestId: z.string().nullable(),
  supplierId: z.string(),
  supplierName: z.string(),
  code: z.string(),
  title: z.string(),
  status: purchaseOrderStatusSchema,
  items: z.array(purchaseOrderItemSchema),
  subtotalCents: z.number().int(),
  taxCents: z.number().int(),
  shippingCents: z.number().int(),
  totalCents: z.number().int(),
  /**
   * Retention in basis points (0..10000). 500 = 5.00%. When > 0, each
   * payment against this PO auto-withholds this fraction of its amount
   * into a warranty holdback released on a separate event.
   */
  retentionPctBp: z.number().int().min(0).max(10_000),
  expectedDeliveryAt: z.string().nullable(),
  supersedesId: z.string().nullable(),
  notes: z.string().nullable(),
  issuedBy: z.string().nullable(),
  issuedByName: z.string().nullable(),
  issuedAt: z.string().nullable(),
  cancelledReason: z.string().nullable(),
  /**
   * Resposta do fornecedor ao pedido, preenchida por ele via portal público.
   * Paralela ao `status` (que é função do recebimento físico). 'pending' até
   * o fornecedor aceitar/recusar. O comprador só lê.
   */
  supplierResponse: z.enum(['pending', 'accepted', 'declined']),
  supplierRespondedAt: z.string().nullable(),
  supplierDeclinedReason: z.string().nullable(),
  /** Nota fiscal anexada pelo fornecedor: número + (opcional) arquivo/data. */
  supplierNfNumber: z.string().nullable(),
  /** true quando há arquivo de NF anexado (chave R2 nunca exposta ao client). */
  supplierNfHasFile: z.boolean(),
  supplierNfAt: z.string().nullable(),
  /** Data em que o fornecedor declarou ter entregue (não é o recebimento). */
  supplierDeliveredAt: z.string().nullable(),
  /**
   * Gate humano de envio. Carimbado quando o pedido é efetivamente enviado
   * ao fornecedor (ato externo que compromete dinheiro). NULL = pedido
   * criado mas ainda não enviado — aparece como "a enviar" na mesa Hoje.
   * A criação do PO (adjudicação manual ou automática) nunca preenche este
   * campo; só `sendPurchaseOrderToSupplier` o faz, após confirmação humana.
   */
  supplierNotifiedAt: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type PurchaseOrder = z.infer<typeof purchaseOrderSchema>;

/**
 * Retention balance for a PO — aggregated from settled payments on the
 * server via v_po_retention. `balanceCents` is the still-held amount
 * (withheld - released) and is what's available to release next.
 */
export const purchaseOrderRetentionSchema = z.object({
  purchaseOrderId: z.string(),
  projectId: z.string(),
  poCode: z.string(),
  retentionPctBp: z.number().int().min(0).max(10_000),
  withheldCents: z.number().int().min(0),
  releasedCents: z.number().int().min(0),
  balanceCents: z.number().int(),
});
export type PurchaseOrderRetention = z.infer<
  typeof purchaseOrderRetentionSchema
>;

export const releasePurchaseOrderRetentionRequestSchema = z.object({
  /**
   * How much of the held balance to release in this event. Server rejects
   * if > current balance so partial releases stay honest.
   */
  amountCents: z.number().int().min(1),
  method: z.enum(['pix', 'boleto', 'transfer', 'cash', 'other']),
  pixTxId: z.string().trim().max(120).optional(),
  reference: z.string().trim().max(200).optional(),
  occurredAt: z.string().datetime({ offset: true }).optional(),
  notes: z.string().trim().max(2000).optional(),
});
export type ReleasePurchaseOrderRetentionRequest = z.infer<
  typeof releasePurchaseOrderRetentionRequestSchema
>;

export const createPurchaseOrderRequestSchema = z.object({
  purchaseRequestId: z.string().optional(),
  supplierId: z.string(),
  title: z.string().trim().min(2).max(160),
  items: z.array(purchaseOrderItemSchema).min(1).max(200),
  taxCents: z.number().int().min(0).optional(),
  shippingCents: z.number().int().min(0).optional(),
  /**
   * Optional retention percentage expressed in basis points (500 = 5%).
   * Omit or set to 0 to disable retention for this PO.
   */
  retentionPctBp: z.number().int().min(0).max(10_000).optional(),
  expectedDeliveryAt: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Expected date must be YYYY-MM-DD')
    .optional(),
  notes: z.string().trim().max(2000).optional(),
});
export type CreatePurchaseOrderRequest = z.infer<
  typeof createPurchaseOrderRequestSchema
>;

export const cancelPurchaseOrderRequestSchema = z.object({
  reason: z.string().trim().min(2).max(500),
});
export type CancelPurchaseOrderRequest = z.infer<
  typeof cancelPurchaseOrderRequestSchema
>;
